import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../utils/const";
import DeletePayment from "./DeletePayment";
import EditPayment from "./EditPayment";
import EditWithdrawal from "./EditWithdrawal";
import DeleteWithdrawal from "./DeleteWithdrawal";

const PaymentList = () => {
  const [payments, setPayments] = useState([]);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [editingWithdrawalId, setEditingWithdrawalId] = useState(null);

  const fetchPayments = () => {
    setLoading(true);
    setError(null);

    const schemeType = localStorage.getItem("schemeType");
    const apiUrl =
      schemeType === "kanchan"
        ? "/kanchan/fetch_kanchan_payments.php"
        : "/mandal/fetch_mandal_payments.php";
    apiClient
      .post(apiUrl)
      .then((res) => {
        if (
          res.data.message === 'radha' &&
          Array.isArray(res.data.data.transactions)
        ) {
          setPayments(res.data.data.transactions);
        } else {
          setError("Unexpected response format");
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Network error");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  useEffect(() => {
    const handleSchemeChange = () => {
      fetchPayments();
    };
    window.addEventListener("schemeChanged", handleSchemeChange);
    return () => window.removeEventListener("schemeChanged", handleSchemeChange);
  }, []);

  const getFullName = (user) =>
    [user.first_name, user.middle_name, user.last_name].filter(Boolean).join(" ");

  // ----- Helpers for colors / labels -----
  const deriveColor = (tType, tMethod) => {
    if (tType === "withdrawal") return "#FF6C6E"; // red
    if (tType === "payment") {
      if (tMethod === "Waiting") return "#EEC0FF"; // purple
      if (tMethod === "Skipped") return "#FFDCAC"; // orange
      return "#5FD965"; // regular (green)
    }
    return "#6c757d"; // neutral grey fallback
  };

  const getTxnLabel = (tType, tMethod) =>
    tType === "payment"
      ? tMethod === "Waiting"
        ? "Waiting"
        : tMethod === "Skipped"
          ? "Skipped"
          : "Regular"
      : "Withdraw";
  // --------------------------------------

  const filteredUsers = payments.filter((user) => {
    const paidBy = (user.paid_by || user.aname || "").toLowerCase();
    const cardNo = String(user.card_no || "").toLowerCase();
    const transactionType = (user.transaction_type || "").toLowerCase();
    const transactionMethod = (user.transaction_method || "").toLowerCase();
    const txnLabel = getTxnLabel(
      user.transaction_type,
      user.transaction_method
    ).toLowerCase();
    const searchTerm = search.toLowerCase().trim();
    const searchCard = searchTerm.replace(/^#/, "");
    const matchesSearch =
      !searchTerm ||
      paidBy.includes(searchTerm) ||
      cardNo.includes(searchCard) ||
      transactionType.includes(searchTerm) ||
      transactionMethod.includes(searchTerm) ||
      txnLabel.includes(searchTerm);

    // Status filtering based on transaction type and method
    let matchesStatus = true;
    if (statusFilter === "Regular") {
      matchesStatus = user.transaction_type === "payment" &&
        user.transaction_method !== "Waiting" &&
        user.transaction_method !== "Skipped";
    } else if (statusFilter === "Withdraw") {
      matchesStatus = user.transaction_type === "withdrawal";
    } else if (statusFilter === "Skipped") {
      matchesStatus = user.transaction_type === "payment" &&
        user.transaction_method === "Skipped";
    } else if (statusFilter === "Waiting") {
      matchesStatus = user.transaction_type === "payment" &&
        user.transaction_method === "Waiting";
    }
    // "All" status filter matches all transactions

    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredUsers.length / entriesPerPage) || 1;
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  return (
    <div className="page-wrapper">
      <div className="page-content">
        <div className="container-xxl">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col">
                      <h1
                        className="card-title"
                        style={{ fontSize: "1.5rem", fontWeight: "bold" }}
                      >
                        Transactions Details
                      </h1>
                    </div>
                    <div className="col-auto d-flex align-items-center gap-2">
                      <button
                        className="btn"
                        onClick={() => navigate("/AddPayment")}
                        style={{
                          backgroundColor: "#4B2C3B",
                          borderColor: "#4B2C3B",
                          color: "#fff",
                        }}
                      >
                        <i className="fas fa-plus me-1" /> Add Payment
                      </button>
                      <button
                        className="btn"
                        onClick={() => navigate("/AddWithdrawal")}
                        style={{
                          backgroundColor: "#4B2C3B",
                          borderColor: "#4B2C3B",
                          color: "#fff",
                        }}
                      >
                        <i className="fas fa-minus me-1" /> Withdraw Money
                      </button>
                    </div>
                  </div>
                </div>

                <div className="card-body pt-0">
                  <div
                    className="d-flex justify-content-between align-items-center mb-3"
                    style={{ marginTop: "1rem" }}
                  >
                    <div>
                      <select
                        value={entriesPerPage}
                        onChange={(e) => {
                          setEntriesPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="form-select d-inline-block w-auto"
                      >
                        {[5, 10, 20, 50].map((num) => (
                          <option key={num} value={num}>
                            {num}
                          </option>
                        ))}
                      </select>
                      <span className="ms-2">entries per page</span>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      <select
                        value={statusFilter}
                        onChange={(e) => {
                          setStatusFilter(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="form-select d-inline-block w-auto"
                        style={{ minWidth: 120 }}
                      >
                        <option value="All">All</option>
                        <option value="Regular">Regular</option>
                        <option value="Withdraw">Withdraw</option>
                        <option value="Skipped">Skipped</option>
                        <option value="Waiting">Waiting</option>
                      </select>
                      <input
                        type="text"
                        className="form-control w-auto"
                        placeholder="Search"
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setCurrentPage(1);
                        }}
                        style={{ minWidth: 200 }}
                      />
                    </div>
                  </div>

                  <div className="table-responsive">
                    {loading ? (
                      <div className="text-center p-4">Loading...</div>
                    ) : error ? (
                      <div className="alert alert-danger m-3">{error}</div>
                    ) : (
                      <table className="table mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            <th className="text-center px-3 py-3 border-0">Card No</th>
                            <th className="text-center px-3 py-3 border-0">Transaction Date</th>
                            <th className="text-center px-3 py-3 border-0">Transaction Type</th>
                            <th className="text-center px-3 py-3 border-0">Amount</th>
                            {/* <th>Amount Type</th> */}
                            <th className="text-center px-3 py-3 border-0">Paid By</th>
                            <th className="text-center px-3 py-3 border-0">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedUsers.map((user, idx) => {
                            const color =
                              (user.color_code && String(user.color_code).trim()) ||
                              deriveColor(
                                user.transaction_type,
                                user.transaction_method
                              );
                            const label = getTxnLabel(
                              user.transaction_type,
                              user.transaction_method
                            );

                            return (
                              <tr
                                key={user.id || idx}
                                className="border-bottom"
                                style={{
                                  borderBottom: "1px solid var(--bs-border-color)",
                                  transition: "all 0.3s ease",
                                  backgroundColor: idx % 2 === 0 ? "transparent" : "var(--bs-gray-50)",
                                }}
                                onMouseEnter={(e) => {
                                  const tr = e.target.closest("tr");
                                  tr.style.backgroundColor = "var(--bs-primary-bg-subtle)";
                                  tr.style.transform = "translateY(-1px)";
                                  tr.style.boxShadow = "0 4px 20px rgba(0,0,0,0.1)";
                                }}
                                onMouseLeave={(e) => {
                                  const tr = e.target.closest("tr");
                                  const idx = Array.from(tr.parentNode.children).indexOf(tr);
                                  tr.style.backgroundColor = idx % 2 === 0 ? "transparent" : "var(--bs-gray-50)";
                                  tr.style.transform = "translateY(0px)";
                                  tr.style.boxShadow = "none";
                                }}
                              >
                                {/* Card Number Column */}
                                <td className="text-center px-3 py-3 border-0">
                                  <span
                                    className="badge bg-primary bg-gradient px-2 py-1"
                                    style={{
                                      fontSize: "0.8rem",
                                      fontWeight: "600",
                                      borderRadius: "8px",
                                    }}
                                  >
                                    {`#${user.card_no}`}
                                  </span>
                                </td>

                                {/* Transaction Date Column */}
                                <td className="text-center px-3 py-3 border-0">
                                  <span
                                    className="fw-medium"
                                    style={{
                                      fontSize: "0.85rem",
                                      color: "var(--bs-secondary-color)",
                                    }}
                                  >
                                    {user.transaction_date}
                                  </span>
                                </td>

                                {/* Transaction Type Column */}
                                <td className="text-center px-3 py-3 border-0">
                                  <span
                                    style={{
                                      display: "inline-block",
                                      padding: "0.45rem 0.9rem",
                                      borderRadius: "999px",
                                      fontSize: "0.8rem",
                                      fontWeight: 600,
                                      lineHeight: 1,
                                      backgroundColor: "transparent",
                                      border: `2px solid ${color}`,
                                      color,
                                      minWidth: 85,
                                      textAlign: "center",
                                    }}
                                    title={color}
                                  >
                                    {label}
                                  </span>
                                </td>

                                {/* Amount Column */}
                                <td className="text-center px-3 py-3 border-0">
                                  <span
                                    className="badge bg-success bg-gradient px-3 py-2"
                                    style={{
                                      fontSize: "0.85rem",
                                      fontWeight: "600",
                                      borderRadius: "12px",
                                      boxShadow: "0 2px 8px rgba(40, 167, 69, 0.3)",
                                    }}
                                  >
                                    ₹{Number(user.amount).toLocaleString("en-IN")}
                                  </span>
                                </td>

                                {/* Paid By Column */}
                                <td className="text-center px-3 py-3 border-0">
                                  <span
                                    className="fw-medium"
                                    style={{
                                      fontSize: "0.9rem",
                                      color: "var(--bs-body-color)",
                                    }}
                                  >
                                    {user.paid_by || user.aname || "-"}
                                  </span>
                                </td>

                                {/* Actions Column */}
                                <td className="text-center px-3 py-3 border-0">
                                  <div className="btn-group" role="group">
                                    {user.transaction_type === "payment" ? (
                                      <>
                                        <button
                                          type="button"
                                          className="btn btn-outline-primary btn-sm me-1"
                                          title="Edit Payment"
                                          onClick={() => setEditingPaymentId(user.id)}
                                          style={{
                                            borderRadius: "8px",
                                            padding: "6px 12px",
                                            transition: "all 0.2s ease",
                                            border: "1px solid #667eea",
                                            color: "#667eea",
                                            backgroundColor: "transparent",
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = "#667eea";
                                            e.currentTarget.style.color = "white";
                                            e.currentTarget.style.transform = "translateY(-1px)";
                                            e.currentTarget.style.boxShadow = "0 4px 12px rgba(102, 126, 234, 0.3)";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = "transparent";
                                            e.currentTarget.style.color = "#667eea";
                                            e.currentTarget.style.transform = "translateY(0px)";
                                            e.currentTarget.style.boxShadow = "none";
                                          }}
                                        >
                                          <i className="fas fa-edit" style={{ fontSize: "0.8rem" }}></i>
                                        </button>
                                        <DeletePayment
                                          pId={user.id}
                                          userName={getFullName(user)}
                                          cardNo={user.card_no}
                                          onDeleteSuccess={(deletedId) =>
                                            setPayments((prev) =>
                                              prev.filter((u) => u.id !== deletedId)
                                            )
                                          }
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          type="button"
                                          className="btn btn-outline-primary btn-sm me-1"
                                          title="Edit Withdrawal"
                                          onClick={() => setEditingWithdrawalId(user.id)}
                                          style={{
                                            borderRadius: "8px",
                                            padding: "6px 12px",
                                            transition: "all 0.2s ease",
                                            border: "1px solid #667eea",
                                            color: "#667eea",
                                            backgroundColor: "transparent",
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = "#667eea";
                                            e.currentTarget.style.color = "white";
                                            e.currentTarget.style.transform = "translateY(-1px)";
                                            e.currentTarget.style.boxShadow = "0 4px 12px rgba(102, 126, 234, 0.3)";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = "transparent";
                                            e.currentTarget.style.color = "#667eea";
                                            e.currentTarget.style.transform = "translateY(0px)";
                                            e.currentTarget.style.boxShadow = "none";
                                          }}
                                        >
                                          <i className="fas fa-edit" style={{ fontSize: "0.8rem" }}></i>
                                        </button>
                                        <DeleteWithdrawal
                                          wId={user.id}
                                          userName={getFullName(user)}
                                          cardNo={user.card_no}
                                          onDeleteSuccess={(deletedId) =>
                                            setPayments((prev) =>
                                              prev.filter((u) => u.id !== deletedId)
                                            )
                                          }
                                        />
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {!loading && !error && (
                    <div className="d-flex justify-content-center align-items-center mt-3">
                      <button
                        className="btn btn-outline-secondary btn-sm me-2"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      >
                        Prev
                      </button>
                      <span>
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        className="btn btn-outline-secondary btn-sm ms-2"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Edit Payment Modal */}
        {editingPaymentId && (
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-lg modal-dialog-centered animate__animated animate__zoomIn animate__faster">
              <div className="modal-content">
                <div
                  className="modal-header"
                  style={{ backgroundColor: "#4B2C3B", color: "white" }}
                >
                  <h5 className="modal-title">
                    Edit Payment (ID={editingPaymentId})
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setEditingPaymentId(null)}
                  />
                </div>
                <div className="modal-body">
                  <EditPayment
                    paymentId={editingPaymentId}
                    onClose={() => {
                      setEditingPaymentId(null);
                      fetchPayments();
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Withdrawal Modal */}
        {editingWithdrawalId && (
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-lg modal-dialog-centered animate__animated animate__zoomIn animate__faster">
              <div className="modal-content">
                <div
                  className="modal-header"
                  style={{ backgroundColor: "#4B2C3B", color: "white" }}
                >
                  <h5 className="modal-title">
                    Edit Withdrawal (ID={editingWithdrawalId})
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setEditingWithdrawalId(null)}
                  />
                </div>
                <div className="modal-body">
                  <EditWithdrawal
                    withdrawalId={editingWithdrawalId}
                    onClose={() => {
                      setEditingWithdrawalId(null);
                      fetchPayments();
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentList;
